/**
 * External dependencies
 */
import React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';

/**
 * Internal dependencies
 */
import Main from 'components/main';
import DocumentHead from 'data/document-head';
import AccountMessagesBanner from 'components/account-messages-banner';
import PaymentMethodsList from 'modules/me/payment-methods-list';
import InvoiceList from 'modules/me/invoice-list';
import Topbar from 'modules/me/topbar';
import { getBusinessName } from 'state/current-user/selectors';
import AddPaymentMethodModal from 'blocks/add-payment-method-modal';
import Button from 'components/button';
import FlashMessage from 'components/flash-message';
import { getCurrentSupport } from 'state/current-support/selectors';
import { getCurrentRental } from 'state/current-rental/selectors';
import { getCurrentRetailerRental } from 'state/current-retailer-rental/selectors';
import { getCurrentRoam } from 'state/current-roam/selectors';
import { getCurrentTsPay } from 'state/current-tspay/selectors';
import { getCurrentCloudHosting } from 'state/current-cloud-hosting/selectors';
import { getCurrentWeb } from 'state/current-web/selectors';
import { getCurrentDispatch } from 'state/current-dispatch/selectors';
import { getCurrentKiosk } from 'state/current-kiosk/selectors';
import { getCurrentCloudBackup } from 'state/current-cloud-backup/selectors';
import { getCurrentBarcodeLookup } from 'state/current-barcode-lookup/selectors';
import { getCurrentVisualDeck } from 'state/current-visual-deck/selectors';
import { getCurrentUniversalImporter } from 'state/current-universal-importer/selectors';
import Money from 'components/money';
import DateTime from 'components/date-time';
import PlanName from 'components/plan-name';
import SubscriptionStatus from 'components/subscription-status';

/**
 * Style dependencies
 */
import './styles.scss';

const SubscriptionCard = ({ planId, amount, period, status, nextBillingAt, startDate, cancelledAt, hasScheduledChanges, setupFee, actions, notice }) => (
    <div className='sub-card'>
        <div className='sub-card__header'>
            <span className='sub-card__name'><PlanName value={planId} /></span>
            <SubscriptionStatus status={status} />
        </div>
        <div className='sub-card__pricing'>
            <Money value={amount / 100} /> / {period}
            {setupFee > 0 && (
                <span className='sub-card__setup-fee'> + <Money value={setupFee / 100} /> setup</span>
            )}
        </div>
        <div className='sub-card__date'>
            {status === 'active' && <span>Next billing: <DateTime date={nextBillingAt} /></span>}
            {status === 'future' && <span>Starts on: <DateTime date={startDate} /></span>}
            {status === 'non_renewing' && <span>Cancels on: <DateTime date={cancelledAt} /></span>}
            {status === 'cancelled' && <span>This plan will not be renewed.</span>}
            {hasScheduledChanges && <div className='sub-card__scheduled'>* Plan changes on next renewal</div>}
        </div>
        {notice && <div className='sub-card__notice'>{notice}</div>}
        {actions && <div className='sub-card__actions'>{actions}</div>}
    </div>
);

const DashboardSection = ({ title, children }) => (
    <div className='dash-section'>
        <div className='dash-section__header'>
            <h3 className='dash-section__title'>{title}</h3>
        </div>
        <div className='dash-section__body'>{children}</div>
    </div>
);

const Dashboard = ({
    businessName,
    currentSupport,
    currentRental,
    currentRetailerRental,
    currentCloudHosting,
    currentWeb,
    currentDispatch,
    currentKiosk,
    currentCloudBackup,
    currentBarcodeLookup,
    currentRoam,
    currentTsPay,
    currentVisualDeck,
    currentUniversalImporter,
}) => {
    const subscriptions = [];

    if (currentSupport) {
        subscriptions.push(
            <SubscriptionCard
                key='support'
                planId={currentSupport.plan_id}
                amount={get(currentSupport, 'plan_amount', 0)}
                period={get(currentSupport, 'billing_period_unit', '')}
                status={get(currentSupport, 'status')}
                nextBillingAt={get(currentSupport, 'next_billing_at')}
                startDate={get(currentSupport, 'start_date')}
                cancelledAt={get(currentSupport, 'cancelled_at')}
                hasScheduledChanges={get(currentSupport, 'has_scheduled_changes')}
                actions={
                    <>
                        <Button link='/dashboard/support/edit' primary>Change Plan</Button>
                        <Button link='/dashboard/support' secondary>View Details</Button>
                    </>
                }
            />
        );
    }

    (currentRental || []).forEach((rental, i) => {
        const s = rental.subscription;
        subscriptions.push(
            <SubscriptionCard
                key={`rental-${i}`}
                planId={s.plan_id}
                amount={s.plan_amount}
                period={s.billing_period_unit}
                status={s.status}
                nextBillingAt={s.next_billing_at}
                startDate={s.start_date}
                cancelledAt={s.cancelled_at}
                hasScheduledChanges={s.has_scheduled_changes}
                notice={s.status === 'cancelled' && (
                    <FlashMessage warning>To re-subscribe to a Rental plan, please contact our account team (<a href='mailto:accounts@towersystems.com.au' style={{ color: 'inherit' }}>accounts@towersystems.com.au</a>)</FlashMessage>
                )}
                actions={(s.plan_id.startsWith('rental#retailer-rental') || s.plan_id.startsWith('rental#nz-retailer-rental')) && (
                    <>
                        <Button link='/dashboard/rental' secondary>View Details</Button>
                    </>
                )}
            />
        );
    });

    (currentCloudBackup || []).forEach((backup, i) => {
        subscriptions.push(
            <SubscriptionCard
                key={`cloud-backup-${i}`}
                planId={backup.plan_id}
                amount={backup.plan_amount}
                period={backup.billing_period_unit}
                status={backup.status}
                nextBillingAt={backup.next_billing_at}
                startDate={backup.start_date}
                cancelledAt={backup.cancelled_at}
                hasScheduledChanges={backup.has_scheduled_changes}
                setupFee={backup.setup_fee}
                actions={<Button link='/dashboard/cloud-backup' secondary>View Details</Button>}
            />
        );
    });

    (currentCloudHosting || []).forEach((hosting, i) => {
        const s = hosting.subscription;
        subscriptions.push(
            <SubscriptionCard
                key={`cloud-hosting-${i}`}
                planId={s.plan_id}
                amount={s.plan_amount}
                period={s.billing_period_unit}
                status={s.status}
                nextBillingAt={s.next_billing_at}
                startDate={s.start_date}
                cancelledAt={s.cancelled_at}
                hasScheduledChanges={s.has_scheduled_changes}
                actions={<Button link='/dashboard/cloud-hosting' secondary>View Details</Button>}
            />
        );
    });

    (currentRoam || []).forEach((roam, i) => {
        const s = roam.subscription;
        subscriptions.push(
            <SubscriptionCard
                key={`roam-${i}`}
                planId={s.plan_id}
                amount={s.plan_amount}
                period={s.billing_period_unit}
                status={s.status}
                nextBillingAt={s.next_billing_at}
                startDate={s.start_date}
                cancelledAt={s.cancelled_at}
                hasScheduledChanges={s.has_scheduled_changes}
                actions={<Button link='/dashboard/roam' secondary>View Details</Button>}
            />
        );
    });

    (currentTsPay || []).forEach((tsPay, i) => {
        const s = tsPay.subscription;
        subscriptions.push(
            <SubscriptionCard
                key={`tspay-${i}`}
                planId={s.plan_id}
                amount={s.plan_amount}
                period={s.billing_period_unit}
                status={s.status}
                nextBillingAt={s.next_billing_at}
                startDate={s.start_date}
                cancelledAt={s.cancelled_at}
                hasScheduledChanges={s.has_scheduled_changes}
                actions={<Button link='/dashboard/tspay' secondary>View Details</Button>}
            />
        );
    });

    if (currentWeb) {
        subscriptions.push(
            <SubscriptionCard
                key='web'
                planId={currentWeb.plan_id}
                amount={get(currentWeb, 'plan_amount', 0)}
                period={get(currentWeb, 'billing_period_unit', '')}
                status={get(currentWeb, 'status')}
                nextBillingAt={get(currentWeb, 'next_billing_at')}
                startDate={get(currentWeb, 'start_date')}
                cancelledAt={get(currentWeb, 'cancelled_at')}
                hasScheduledChanges={get(currentWeb, 'has_scheduled_changes')}
                actions={<Button link='/dashboard/shopify-development' secondary>View Details</Button>}
            />
        );
    }

    (currentVisualDeck || []).forEach((vd, i) => {
        const s = vd.subscription;
        subscriptions.push(
            <SubscriptionCard
                key={`visual-deck-${i}`}
                planId={s.plan_id}
                amount={s.plan_amount}
                period={s.billing_period_unit}
                status={s.status}
                nextBillingAt={s.next_billing_at}
                startDate={s.start_date}
                cancelledAt={s.cancelled_at}
                hasScheduledChanges={s.has_scheduled_changes}
                actions={<Button link='/dashboard/visual-deck' secondary>View Details</Button>}
            />
        );
    });

    (currentUniversalImporter || []).forEach((ui, i) => {
        const s = ui.subscription;
        subscriptions.push(
            <SubscriptionCard
                key={`universal-importer-${i}`}
                planId={s.plan_id}
                amount={s.plan_amount}
                period={s.billing_period_unit}
                status={s.status}
                nextBillingAt={s.next_billing_at}
                startDate={s.start_date}
                cancelledAt={s.cancelled_at}
                hasScheduledChanges={s.has_scheduled_changes}
                actions={<Button link='/dashboard/universal-importer' secondary>View Details</Button>}
            />
        );
    });

    return (
        <div>
            <Topbar
                title='Billing Dashboard'
                subTitle={businessName ? `Hello, ${businessName}` : 'Hello'}
                selected='dashboard'
            />
            <DocumentHead title='Billing - Tower Systems' />
            <Main padding='xxlarge'>
                <div className='dashboard'>

                    <AccountMessagesBanner />

                    <DashboardSection title='Payment Method'>
                        <PaymentMethodsList />
                    </DashboardSection>

                    <DashboardSection title='Current Subscriptions'>
                        {subscriptions.length > 0 ? (
                            <div className='sub-card-grid'>
                                {subscriptions}
                            </div>
                        ) : (
                            <div className='dash-empty'>
                                <p>You have no active subscriptions.</p>
                                <Button primary link='/dashboard/plans'>Browse plans</Button>
                            </div>
                        )}
                        <div className='dash-section__cta'>
                            <Button primary link='/dashboard/plans'>+ Subscribe to a plan</Button>
                        </div>
                    </DashboardSection>

                    <DashboardSection title='Billing History'>
                        <InvoiceList />
                    </DashboardSection>

                </div>
            </Main>
            <AddPaymentMethodModal />
        </div>
    );
};

export default connect(
    (state) => ({
        businessName: getBusinessName(state),
        currentSupport: getCurrentSupport(state),
        currentRental: getCurrentRental(state),
        currentRetailerRental: getCurrentRetailerRental(state),
        currentCloudHosting: getCurrentCloudHosting(state),
        currentWeb: getCurrentWeb(state),
        currentDispatch: getCurrentDispatch(state),
        currentKiosk: getCurrentKiosk(state),
        currentCloudBackup: getCurrentCloudBackup(state),
        currentBarcodeLookup: getCurrentBarcodeLookup(state),
        currentRoam: getCurrentRoam(state),
        currentTsPay: getCurrentTsPay(state),
        currentVisualDeck: getCurrentVisualDeck(state),
        currentUniversalImporter: getCurrentUniversalImporter(state),
    }),
    {},
)(Dashboard);
